/**
 * Annotations panel (doc 147 §8.4, §9 Phase 4, doc 148 SEC-03).
 *
 * Threaded comments for an entity, embedded in the entity detail view.
 * Presentational — `threads` and the save/resolve callbacks are injected.
 * Message bodies are sanitized with DOMPurify on render (the write path is
 * stripped server-side).
 */
import { useState } from 'react';
import DOMPurify from 'dompurify';
import type { AnnotationThread } from '../state/atlasApiTypes.js';
import { Icon } from '../components/Icon.js';

export interface AnnotationsPanelProps {
  entityId: string;
  threads: AnnotationThread[];
  /** False for read-only roles (none today, but future-proofs the UI). */
  canWrite: boolean;
  onSave: (threadId: string, body: string) => Promise<void>;
  onResolve: (threadId: string, resolved: boolean) => Promise<void>;
}

/** Render-time sanitization — strips any HTML the body might carry. */
export function renderSafeBody(body: string): string {
  return DOMPurify.sanitize(body, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
}

/** Open vs resolved counts for the section header. */
export function summarizeThreads(threads: AnnotationThread[]): { open: number; resolved: number } {
  return {
    open: threads.filter((t) => !t.resolved).length,
    resolved: threads.filter((t) => t.resolved).length,
  };
}

export function AnnotationsPanel({
  entityId,
  threads,
  canWrite,
  onSave,
  onResolve,
}: AnnotationsPanelProps): React.JSX.Element {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const counts = summarizeThreads(threads);

  const addThread = async (): Promise<void> => {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      const threadId = crypto.randomUUID?.() ?? `t-${Date.now()}`;
      await onSave(threadId, body);
      setDraft('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="annotations-panel">
      <div className="section-label">
        Annotations · {counts.open} open
        {counts.resolved > 0 && ` · ${counts.resolved} resolved`}
      </div>

      {threads.map((thread) => (
        <div key={thread.id} className="card" style={{ marginBottom: 'var(--sp-sm)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)' }}>
            <Icon
              name={thread.resolved ? 'task_alt' : 'edit_note'}
              size="sm"
              color={thread.resolved ? 'var(--good)' : undefined}
            />
            <span className="ref-kind">{thread.resolved ? 'resolved' : 'open'}</span>
            {canWrite && (
              <button
                className="ref-kind"
                style={{ marginLeft: 'auto' }}
                onClick={() => void onResolve(thread.id, !thread.resolved)}
              >
                {thread.resolved ? 'reopen' : 'resolve'}
              </button>
            )}
          </div>
          {thread.messages.map((msg) => (
            <div key={msg.id} style={{ marginTop: 'var(--sp-sm)' }}>
              <span className="mono" style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>
                {msg.authorEmail}
              </span>
              <p style={{ color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>
                {renderSafeBody(msg.body)}
              </p>
            </div>
          ))}
        </div>
      ))}

      {canWrite && (
        <div style={{ display: 'flex', gap: 'var(--sp-sm)', marginTop: 'var(--sp-sm)' }}>
          <input
            className="filter-chip"
            style={{ flex: 1 }}
            aria-label={`Add an annotation to ${entityId}`}
            placeholder="Add a note — @mention to notify"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void addThread();
            }}
          />
          <button className="btn btn-ghost" onClick={() => void addThread()} disabled={busy}>
            {busy ? 'Saving…' : 'Comment'}
          </button>
        </div>
      )}
    </section>
  );
}
